/**
 * What a traced shape IS.
 *
 * The importer used to throw away everything that wasn't a colour-filled shop,
 * on the reasoning that only leasable area belongs in a leasing tool. That is
 * true of the COMMERCIALS and wrong about the MODEL: a mall drawn as nothing
 * but its shops reads as a floor with holes punched through it, because the
 * promenades, atria, cores and back-of-house that hold it together are missing.
 *
 * So now every enclosed shape on the sheet is traced and kept, and each one
 * carries a kind. The kind decides three separate things:
 *
 *   LEASABLE   does it get a lease, sales, a rank and a place in the dashboard
 *   WALKABLE   can a route cross it
 *   how it is drawn in 3D — extruded shopfront, floor plate, recessed void
 *
 * Detection proposes a kind; the operator corrects it. Nothing here is a
 * guess the user cannot see and override.
 */

export const REGION_KINDS = [
    {
        id: 'tenancy',
        label: 'Retail unit',
        hint: 'Leasable. Gets a lease, sales and a place in the dashboard.',
        leasable: true,
        walkable: false,
        height: 4.2,
        swatch: '#EC4899',
    },
    {
        id: 'circulation',
        label: 'Mall / corridor',
        hint: 'Walkable floor. Routes run along these.',
        leasable: false,
        walkable: true,
        height: 0.14,
        swatch: '#22D3EE',
    },
    {
        id: 'amenity',
        label: 'Amenity / core',
        hint: 'Lifts, escalators, restrooms. Walkable and routable.',
        leasable: false,
        walkable: true,
        height: 1.1,
        swatch: '#38BDF8',
    },
    {
        id: 'void',
        label: 'Atrium void',
        hint: 'Open to the level below. Drawn recessed, not walkable.',
        leasable: false,
        walkable: false,
        height: -0.35,
        swatch: '#475569',
    },
    {
        id: 'service',
        label: 'Back of house',
        hint: 'Plant, storage, service corridors. Modelled but not leasable.',
        leasable: false,
        walkable: false,
        height: 3,
        swatch: '#64748B',
    },
    {
        id: 'landscape',
        label: 'Landscape / water',
        hint: 'Planting, pools, soft landscape. Drawn flat.',
        leasable: false,
        walkable: false,
        height: 0.25,
        swatch: '#34D399',
    },
    {
        id: 'exterior',
        label: 'Outside the building',
        hint: 'Car park, apron, the rest of the sheet. Drawn flat and muted.',
        leasable: false,
        walkable: false,
        height: 0.04,
        swatch: '#334155',
    },
    {
        id: 'ignore',
        label: 'Not part of the plan',
        hint: 'Title blocks, keys, legends. Dropped at publish.',
        leasable: false,
        walkable: false,
        height: 0,
        swatch: '#1E293B',
        hidden: true,
    },
];

export const KIND_BY_ID = new Map(REGION_KINDS.map((k) => [k.id, k]));
export const kindOf = (id) => KIND_BY_ID.get(id) || KIND_BY_ID.get('tenancy');
export const isLeasable = (id) => !!kindOf(id).leasable;
export const isWalkable = (id) => !!kindOf(id).walkable;
export const isDrawn = (id) => !kindOf(id).hidden;

/** Ordered for the picker: the three you actually use, then the rest. */
export const KIND_PICKER_ORDER = [
    'tenancy', 'circulation', 'amenity', 'void', 'service', 'landscape', 'exterior', 'ignore',
];

// ── walkable model ──────────────────────────────────────────────────────────

/*
 * Shoelace area of a normalised polygon. geometry.js has this, but importing it
 * would be the only dependency this module has, and it is deliberately free of
 * them so the classification can be lifted on its own -- so eight lines are
 * cheaper than the coupling.
 */
function area(poly) {
    if (!poly || poly.length < 3) return 0;
    let a = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        a += (poly[j][0] * poly[i][1]) - (poly[i][0] * poly[j][1]);
    }
    return Math.abs(a / 2);
}

/**
 * Rasterise the walkable kinds into a routing grid.
 *
 * Derived from the CLASSIFIED regions rather than from pixel colour, so the
 * moment an operator marks a shape as corridor it becomes routable, and the
 * moment they mark one as void the routes stop crossing it.
 *
 * Painted LARGEST SHAPE FIRST, so each shape overrides whatever contains it.
 *
 * That ordering is the whole correctness of this function. Shapes traced from a
 * plan are nested: the sheet contains the building, the building contains the
 * mall floor, the mall floor contains the shops and the atrium void. Painting
 * big-to-small is exactly that containment, and it reconstructs the holes in a
 * promenade for free -- a mall's traced outline is its OUTER boundary, with the
 * shops standing in it as holes rather than separate cut-outs.
 *
 * This used to fill every walkable shape and then erase every non-walkable one.
 * That handles a shop sitting INSIDE the promenade, but has no defence against a
 * blocker that CONTAINS it -- and most sheets have one, because the background
 * field of the drawing traces as a single shape covering essentially the whole
 * page and classifies as `exterior`. On the reference plan that shape covered
 * 99.9% of the sheet and erased 100% of the 3,902 promenade cells, so every
 * import reported "no walkable space was traced" and no route could ever be
 * computed. Painting by area fixes it without a special case: the background is
 * simply the first thing painted and everything else lands on top.
 */
export function buildWalkableGrid(regions, planAspect, gridEdge = 168, options = {}) {
    const { minIslandFraction = 0.02, inside = null, confineToBuilding = true } = options;

    // Match the mask's grid exactly when there is one, so the two can be
    // intersected cell for cell.
    const gw = inside ? inside.w : Math.max(8, Math.round(planAspect >= 1 ? gridEdge : gridEdge * planAspect));
    const gh = inside ? inside.h : Math.max(8, Math.round(planAspect >= 1 ? gridEdge / planAspect : gridEdge));
    const cells = new Uint8Array(gw * gh);

    const drawn = regions.filter((r) => isDrawn(r.kind));
    drawn
        .map((r) => ({ r, a: area(r.polygon || []) }))
        .sort((p, q) => q.a - p.a)
        .forEach(({ r }) => fillPolygon(cells, gw, gh, r.polygon, isWalkable(r.kind) ? 1 : 0));

    // A promenade shape usually runs off the sheet, because on most plans the
    // mall and the apron outside it are one continuous wash. Clipping to the
    // building keeps routes indoors without asking the operator to somehow
    // split a shape the drawing never separated.
    if (inside && confineToBuilding) {
        const mask = unpackBits(inside.bits, gw * gh);
        for (let i = 0; i < cells.length; i++) if (!mask[i]) cells[i] = 0;
    }

    // Islands are places a door can attach and then route nowhere, which reads
    // to the user as "directions are broken".
    pruneIslands(cells, gw, gh, minIslandFraction);

    let filled = 0;
    for (let i = 0; i < cells.length; i++) filled += cells[i];
    if (!filled) return null;

    return { w: gw, h: gh, filled, bits: packBits(cells) };
}

/** Even-odd scanline fill of a normalised polygon into a grid. */
function fillPolygon(cells, gw, gh, polygon, value) {
    if (!polygon || polygon.length < 3) return;
    const pts = polygon.map(([x, y]) => [x * gw, y * gh]);

    let minY = Infinity;
    let maxY = -Infinity;
    pts.forEach(([, y]) => { if (y < minY) minY = y; if (y > maxY) maxY = y; });
    const y0 = Math.max(0, Math.floor(minY));
    const y1 = Math.min(gh - 1, Math.ceil(maxY));

    const xs = [];
    for (let gy = y0; gy <= y1; gy++) {
        const sy = gy + 0.5;
        xs.length = 0;
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            const [xi, yi] = pts[i];
            const [xj, yj] = pts[j];
            if ((yi > sy) !== (yj > sy)) {
                xs.push(xi + (((sy - yi) / ((yj - yi) || 1e-12)) * (xj - xi)));
            }
        }
        if (xs.length < 2) continue;
        xs.sort((a, b) => a - b);
        for (let k = 0; k + 1 < xs.length; k += 2) {
            const sx0 = Math.max(0, Math.ceil(xs[k] - 0.5));
            const sx1 = Math.min(gw - 1, Math.floor(xs[k + 1] - 0.5));
            for (let gx = sx0; gx <= sx1; gx++) cells[(gy * gw) + gx] = value;
        }
    }
}

function pruneIslands(cells, gw, gh, minFraction) {
    const owner = new Int32Array(cells.length).fill(-1);
    const sizes = [];
    const stack = new Int32Array(cells.length);
    const NB = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];

    let total = 0;
    for (let seed = 0; seed < cells.length; seed++) {
        if (!cells[seed] || owner[seed] >= 0) continue;
        const id = sizes.length;
        let sp = 0;
        stack[sp++] = seed;
        owner[seed] = id;
        let n = 0;
        while (sp > 0) {
            const i = stack[--sp];
            n += 1;
            const x = i % gw;
            const y = (i - x) / gw;
            for (let k = 0; k < NB.length; k++) {
                const nx = x + NB[k][0];
                const ny = y + NB[k][1];
                if (nx < 0 || ny < 0 || nx >= gw || ny >= gh) continue;
                const j = (ny * gw) + nx;
                if (cells[j] && owner[j] < 0) { owner[j] = id; stack[sp++] = j; }
            }
        }
        sizes.push(n);
        total += n;
    }

    const floor = total * minFraction;
    for (let i = 0; i < cells.length; i++) {
        if (cells[i] && sizes[owner[i]] < floor) cells[i] = 0;
    }
}

// ── bit packing ─────────────────────────────────────────────────────────────

/** One cell per bit, base64'd: a 168² grid costs ~4 KB instead of ~28 KB. */
export function packBits(cells) {
    const bytes = new Uint8Array(Math.ceil(cells.length / 8));
    for (let i = 0; i < cells.length; i++) if (cells[i]) bytes[i >> 3] |= 1 << (i & 7);
    let bin = '';
    for (let i = 0; i < bytes.length; i += 4096) {
        bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 4096));
    }
    return typeof btoa === 'function' ? btoa(bin) : Buffer.from(bytes).toString('base64');
}

export function unpackBits(b64, length) {
    const cells = new Uint8Array(length);
    let bytes;
    if (typeof atob === 'function') {
        const bin = atob(b64);
        bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
        bytes = new Uint8Array(Buffer.from(b64, 'base64'));
    }
    for (let i = 0; i < length; i++) cells[i] = (bytes[i >> 3] >> (i & 7)) & 1;
    return cells;
}
